// Common field
import { SubsReportFieldCommon } from '@/features/subscribers-report/common/subs-report-field.common'

/**
 * @typedef {import('@/clients/BackendAdminClient/subscriber-report').SubsReportItem} SubsReportItem
 * @typedef {import('@/features/subscribers-report/common/subs-report-field.common').Label} Label
 */

/**
 * @typedef {function(SubsReportItem): (String|Number|null)} Cell
 */

/**
 * @typedef Group
 * @type {Object}
 * @property {String[]} fixed
 * @property {String[]} user
 * @property {String[]} subscription
 * @property {String[]} pricing
 * @property {String[]} service
 * @property {String[]} appointment
 * @property {String[]} receipt
 * @property {String[]} sap
 * @property {String[]} mnc
 */

// Common field
const { labels, groups } = SubsReportFieldCommon

export class SubsReportMapperCommon {

  /** @type {Label} Field keys in column order */
  static labels = labels

  /** @type {GroupKey} Group keys */
  static groups = groups

  /** @type {GroupKey} Group display labels */
  static groupLabels = {
    [groups.fixed]       : 'ID ผู้ใช้',
    [groups.user]        : 'ผู้ใช้งาน',
    [groups.subscription]: 'การสมัครสมาชิก',
    [groups.service]     : 'บริการ',
    [groups.appointment] : 'การนัดหมาย',
    [groups.pricing]     : 'ราคา',
    [groups.receipt]     : 'ใบเสร็จ',
    [groups.sap]         : 'SAP',
    [groups.mnc]         : 'MNC',
  }

  /** @type {Label} Field display labels */
  static headerLabels = {

    // user
    [labels.userId]                 : 'ID',
    [labels.name]                   : 'ชื่อ',
    [labels.gender]                 : 'เพศ',
    [labels.mobile]                 : 'เบอร์ติดต่อ',
    [labels.birthYear]              : 'ปีเกิด',
    [labels.taxId]                  : 'TaxID',
    
    // subscription
    [labels.orderNo]                : 'ลำดับ',
    [labels.projectName]            : 'โครงการ',
    [labels.address]                : 'ที่อยู่',
    [labels.category]               : 'หมวดหมู่',
    [labels.packageId]              : 'ID',
    [labels.packageName]            : 'แพ็กเกจ',
    [labels.optMain]                : 'Option หลัก',
    [labels.optExtra]               : 'Option เสริม',
    [labels.seller]                 : 'บริษัท',
    [labels.type]                   : 'ประเภท',
    [labels.txnStatus]              : 'สถานะ Transaction',
    [labels.billingStatus]          : 'สถานะตามรอบบิล',
    [labels.actualStatus]           : 'สถานะ',

    // pricing
    [labels.paymentDate]            : 'วันชำระ',
    [labels.paymentTime]            : 'เวลาที่ชำระ',
    [labels.dateStart]              : 'วันเริ่ม',
    [labels.dateEnd]                : 'วันสิ้นสุด',
    [labels.optMainPrice]           : 'ราคา Option หลัก',
    [labels.optExtraPrice]          : 'ราคา Option เสริม',
    [labels.quantity]               : 'จำนวนต่อ/แพ็กเกจ',
    [labels.sellingPriceAfterVat]   : 'ราคาขาย (หลัง VAT)',
    [labels.discount]               : 'ส่วนลด',
    [labels.netSellingPriceAfterVat]: 'ราคาขายสุทธิ (หลัง VAT)',
    [labels.vat]                    : 'VAT',
    [labels.sellingPriceBeforeVat]  : 'ราคาขาย (ก่อน VAT)',
    [labels.netIncomeBeforeMdr]     : 'เงินรับสุทธิ (ก่อน MDR + VAT)',
    [labels.mdrAndVat]              : 'MDR และ VAT',
    [labels.feeRateText]            : 'อัตราค่าธรรมเนียม',
    [labels.netFee]                 : 'ค่าธรรมเนียมสุทธิ',
    [labels.netIncome]              : 'เงินรับสุทธิ',

    // service
    [labels.serviceName]            : 'บริการ',
    [labels.serviceProviderName]    : 'ผู้ให้บริการ',
    [labels.serviceCostBeforeVat]   : 'ต้นทุนประมาณการ (ก่อน VAT)',
    [labels.serviceCostAfterVat]    : 'ต้นทุนประมาณการ (หลัง VAT)',

    // appointment
    [labels.bookedAt]               : 'นัดเมื่อ',
    [labels.performedAt]            : 'วันนัด',
    [labels.performedTime]          : 'เวลานัด',
    [labels.confirmedAt]            : 'ยืนยันเมื่อ',
    [labels.usedAt]                 : 'ใช้เมื่อ',
    [labels.closedAt]               : 'ปิดงาน',
    [labels.appointmentProviderName]: 'ชื่อผู้ให้บริการรายนัดหมาย',
    [labels.actualCost]             : 'ต้นทุนจริง',
    [labels.maintNo]                : 'เลขที่ใบแจ้งซ่อม',
    [labels.hasRemark]              : 'หมายเหตุ',

    // receipt
    [labels.creditNoteNo]           : 'เลขที่ใบลดหนี้',
    [labels.paymentChannel]         : 'ช่องทางการชำระเงิน',
    [labels.receiptInfo]            : 'ข้อมูลที่ใช้ออกใบเสร็จ',
    [labels.receiptNo]              : 'เลขที่ใบเสร็จ',
    [labels.refCode]                : 'รหัสอ้างอิง',
    [labels.note]                   : 'หมายเหตุ',

    // sap
    [labels.sapServiceType]         : 'ประเภทของสินค้าและบริการ',
    [labels.sapProviderName]        : 'ผู้ให้บริการ',
    [labels.sapPrice]               : 'ราคาต้นทุน',
    [labels.purchaseOrder]          : 'เลขที่ใบสั่งซื้อ (PO)',
    [labels.vendorReceiptNo]        : 'เลขที่ใบเสร็จจาก Vendor',
    [labels.vendorReceiptDate]      : 'วันที่ใบเสร็จจาก Vendor',

    // mnc
    [labels.coinAmount]             : 'จำนวน MNC',
    [labels.coinValue]              : 'มูลค่า MNC (บาท)',
    [labels.coinWallet]             : 'Wallet Name',
  }

  /** @type {Group} Field keys grouped by table section */
  static groupFields = {

    // fixed
    [groups.fixed]: [
      labels.userId,
    ],

    // user
    [groups.user]: [
      labels.name,
      labels.gender,
      labels.mobile,
      labels.birthYear,
      labels.taxId
    ],

    // subscription
    [groups.subscription]: [
      labels.orderNo,
      labels.packageId,
      labels.type,
      labels.category,
      labels.seller,
      labels.packageName,
      labels.optMain,
      labels.optExtra,
      labels.quantity,
      labels.projectName,
      labels.address,
      labels.paymentDate,
      labels.paymentTime,
      labels.dateStart,
      labels.dateEnd,
      labels.txnStatus,
      labels.billingStatus,
      labels.actualStatus,
    ],

    // service
    [groups.service]: [
      labels.serviceName,
      labels.serviceProviderName,
      labels.serviceCostBeforeVat,
      labels.serviceCostAfterVat,
    ],

    // appointment
    [groups.appointment]: [
      labels.bookedAt,
      labels.performedAt,
      labels.performedTime,
      labels.confirmedAt,
      labels.usedAt,
      labels.closedAt,
      labels.appointmentProviderName,
      labels.maintNo,
      labels.hasRemark,
      labels.actualCost,
    ],

    // pricing
    [groups.pricing]: [
      labels.optMainPrice,
      labels.optExtraPrice,
      labels.sellingPriceAfterVat,
      labels.discount,
      labels.netSellingPriceAfterVat,
      labels.vat,
      labels.sellingPriceBeforeVat,
      labels.netIncomeBeforeMdr,
      labels.mdrAndVat,
      labels.feeRateText,
      labels.netFee,
      labels.netIncome,
    ],

    // receipt
    [groups.receipt]: [
      labels.paymentChannel,
      labels.receiptNo,
      labels.paymentDate,
      labels.paymentTime,
      labels.receiptInfo,
      labels.refCode,
      labels.creditNoteNo,
      labels.note,
    ],

    // sap
    [groups.sap]: [
      labels.sapServiceType,
      labels.sapProviderName,
      labels.sapPrice,
      labels.purchaseOrder,
      labels.vendorReceiptNo,
      labels.vendorReceiptDate,
    ],

    // mnc
    [groups.mnc]: [
      labels.coinWallet,
      labels.coinAmount,
      labels.coinValue,
    ],
  }

  /** @type {Object<Number, Cell>} */
  static fields = {
    
    // user
    [labels.userId]                 : this.value('user', 'userId'),
    [labels.name]                   : this.value('user', 'name'),
    [labels.gender]                 : this.value('user', 'gender'),
    [labels.mobile]                 : this.value('user', 'mobile'),
    [labels.birthYear]              : this.value('user', 'birthYear'),
    [labels.taxId]                  : this.value('user', 'taxId'),
    
    // subscription
    [labels.orderNo]                : this.value('subscription', 'orderNo'),
    [labels.projectName]            : this.value('subscription', 'projectName'),
    [labels.address]                : this.value('subscription', 'address'),
    [labels.category]               : record => this.category(record),
    [labels.packageId]              : this.value('subscription', 'packageId'),
    [labels.packageName]            : this.value('subscription', 'packageName'),
    [labels.seller]                 : this.value('subscription', 'seller'),
    [labels.type]                   : this.value('subscription', 'type'),
    [labels.txnStatus]              : this.value('subscription', 'txnStatus'),
    [labels.billingStatus]          : this.value('subscription', 'billingStatus'),
    [labels.actualStatus]           : record => this.actualStatus(record),

    // service
    [labels.serviceName]            : this.value('service', 'name'),
    [labels.serviceProviderName]    : this.value('service', 'providerName'),
    [labels.serviceCostBeforeVat]   : this.value('service', 'costBeforeVat'),
    [labels.serviceCostAfterVat]    : this.value('service', 'costAfterVat'),

    // appointment
    [labels.bookedAt]               : this.value('appointment', 'bookedAt'),
    [labels.performedAt]            : this.value('appointment', 'performedAt'),
    [labels.performedTime]          : this.value('appointment', 'performedTime'),
    [labels.confirmedAt]            : this.value('appointment', 'confirmedAt'),
    [labels.usedAt]                 : this.value('appointment', 'usedAt'),
    [labels.closedAt]               : this.value('appointment', 'closedAt'),
    [labels.appointmentProviderName]: this.value('appointment', 'providerName'),
    [labels.actualCost]             : this.value('appointment', 'actualCost'),
    [labels.maintNo]                : this.value('appointment', 'maintNo'),
    [labels.hasRemark]              : this.value('appointment', 'hasRemark'),

    // pricing
    [labels.paymentDate]            : this.value('pricing', 'paymentDate'),
    [labels.paymentTime]            : this.value('pricing', 'paymentTime'),
    [labels.dateStart]              : this.value('pricing', 'dateStart'),
    [labels.dateEnd]                : this.value('pricing', 'dateEnd'),
    [labels.optMain]                : this.value('pricing', 'optMain'),
    [labels.optExtra]               : this.value('pricing', 'optExtra'),
    [labels.optMainPrice]           : this.value('pricing', 'optMainPrice'),
    [labels.optExtraPrice]          : this.value('pricing', 'optExtraPrice'),
    [labels.quantity]               : this.value('pricing', 'quantity'),
    [labels.sellingPriceAfterVat]   : this.value('pricing', 'sellingPriceAfterVat'),
    [labels.discount]               : record => this.discount(record),
    [labels.netSellingPriceAfterVat]: this.value('pricing', 'netSellingPriceAfterVat'),
    [labels.vat]                    : this.value('pricing', 'vat'),
    [labels.sellingPriceBeforeVat]  : this.value('pricing', 'sellingPriceBeforeVat'),
    [labels.netIncomeBeforeMdr]     : this.value('pricing', 'netIncomeBeforeMdr'),
    [labels.mdrAndVat]              : this.value('pricing', 'mdrAndVat'),
    [labels.feeRateText]            : this.value('pricing', 'feeRateText'),
    [labels.netFee]                 : this.value('pricing', 'netFee'),
    [labels.netIncome]              : this.value('pricing', 'netIncome'),

    // receipt
    [labels.creditNoteNo]           : this.value('receipt', 'creditNoteNo'),
    [labels.paymentChannel]         : this.value('receipt', 'paymentChannel'),
    [labels.receiptInfo]            : record => this.receiptInfo(record),
    [labels.receiptNo]              : record => this.receiptNo(record),
    [labels.refCode]                : this.value('receipt', 'refCode'),
    [labels.note]                   : this.value('receipt', 'note'),

    // sap
    [labels.sapProviderName]        : this.value('sap', 'providerName'),
    [labels.sapServiceType]         : this.value('sap', 'serviceType'),
    [labels.vendorReceiptDate]      : this.value('sap', 'vendorReceiptDate'),
    [labels.vendorReceiptNo]        : this.value('sap', 'vendorReceiptNo'),
    [labels.purchaseOrder]          : this.value('sap', 'purchaseOrder'),
    [labels.sapPrice]               : this.value('sap', 'price'),

    // mnc
    [labels.coinAmount]             : this.value('mnc', 'coinAmount'),
    [labels.coinValue]              : this.value('mnc', 'coinValue'),
    [labels.coinWallet]             : this.value('mnc', 'coinWallet'),
  }

  // Key by groups
  static keys = Object.values(this.groupFields).flat()

  // Fields blanked out on non-start rows of a user group
  static blankFields = [

    // user
    labels.userId,
    labels.name,
    labels.gender,
    labels.mobile,
    labels.birthYear,
    labels.taxId,

    // subs
    labels.orderNo,
    labels.packageId,
    labels.type,
    labels.category,
    labels.seller,
    labels.packageName,
    labels.optMain,
    labels.optExtra,
    labels.quantity,
    labels.projectName,
    labels.address,
    labels.dateStart,
    labels.dateEnd,
    labels.txnStatus,
    labels.billingStatus,
    labels.actualStatus,

    // pricing
    labels.optMainPrice,
    labels.optExtraPrice,
    labels.sellingPriceAfterVat,
    labels.discount,
    labels.netSellingPriceAfterVat,
    labels.vat,
    labels.sellingPriceBeforeVat,
    labels.netIncomeBeforeMdr,
    labels.mdrAndVat,
    labels.feeRateText,
    labels.netFee,
    labels.netIncome,

    // receipt
    labels.receiptNo,
    labels.creditNoteNo,
    labels.paymentDate,
    labels.paymentTime,
    labels.paymentChannel,
    labels.receiptInfo,
    labels.refCode,
    labels.note,

    // mnc
    labels.coinWallet,
    labels.coinAmount,
    labels.coinValue,
  ]

  // Fields rendered as currency amounts
  static currencyFields = [

    // pricing
    labels.optMainPrice,
    labels.optExtraPrice,
    labels.sellingPriceAfterVat,
    labels.netSellingPriceAfterVat,
    labels.vat,
    labels.sellingPriceBeforeVat,
    labels.netIncomeBeforeMdr,
    labels.mdrAndVat,
    labels.netFee,
    labels.netIncome,

    // service
    labels.serviceCostBeforeVat,
    labels.serviceCostAfterVat,

    // appointment
    labels.actualCost,

    // sap
    labels.sapPrice,

    // mnc
    labels.coinValue,
  ]

  // Curried cell mapper
  static value(group, prop) {
    return record => this.fieldValue(
      record, group, prop
    )
  }

  /**
   * Record group field value
   * @param {SubsReportItem} record
   * @param {String} group
   * @param {String} prop
   */
  static fieldValue(record, group, prop) {

    // Record value
    const value = record && record[group]

    return (
      value ? value[prop] ?? null : null
    )
  }

  /** @param {SubsReportItem} record */
  static category(record) {

    // Category items
    const items = this.fieldValue(
      record, 'subscription', 'category'
    )

    // Validate category
    if (!items) return null

    return (
      items.join(',\n')
    )
  }

  /** @param {SubsReportItem} record */
  static discount(record) {

    // Discount title
    const title  = this.fieldValue(
      record, 'pricing', 'discountTitle'
    )
    
    // Discount amout
    const amount = this.fieldValue(
      record, 'pricing', 'discountAmount'
    )

    // Validate discount (0 is valid)
    if (title == null || amount == null) return null

    return (
      `${amount} (${title})`
    )
  }

  /** @param {SubsReportItem} record */
  static actualStatus(record) {

    // Actual status value
    const value = this.fieldValue(
      record, 'subscription', 'actualStatus'
    )

    // Active subscription is displayed as subscribed
    return (
      value === 'ACTIVE' ? 'SUBSCRIBED' : value
    )
  }

  // UUID format matcher
  static uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  /** @param {SubsReportItem} record */
  static receiptNo(record) {

    // Receipt number value
    const value = this.fieldValue(
      record, 'receipt', 'receiptNo'
    )

    // Non-uuid values pass through unchanged
    if (!value || !this.uuidPattern.test(value)) return value

    return (
      value.slice(0, 8).toUpperCase()
    )
  }

  /** @param {SubsReportItem} record */
  static receiptInfo(record) {

    // Receipt info value
    const info = this.fieldValue(
      record, 'receipt', 'receiptInfo'
    )

    // Validate receipt info
    if (!info) return null

    // Info props
    const { name, address, taxId, branchNo } = info

    // Receipt info lines
    const lines = [
      `ชื่อ : ${name || ''}`,
      `ที่อยู่ : ${address || ''}`,
      `เลขประจำตัวผู้เสียภาษี : ${taxId || 'N/A'}`,
    ]

    // Branch number line
    if (branchNo) {
      lines.push(`เลขที่สาขา : ${branchNo}`)
    }

    return (
      lines.join('\n')
    )
  }

  /** @param {Number|String} value */
  static currency(value) {

    // Validate value
    if (typeof value !== 'number') {
      return value
    }

    return `${value.toLocaleString('th-TH')} บาท`
  }

  static genderMap = {
    F: 'หญิง',
    M: 'ชาย',
  }

  /** @param {String} value */
  static gender(value) {
    return (
      this.genderMap[value.toUpperCase()] ?? value
    )
  }

  static statusMap = {
    ACTIVE      : 'success',
    SUBSCRIBED  : 'primary',
    EXPIRED     : 'warning',
    UNSUBSCRIBED: 'danger',
    CANCELLED   : 'danger',
  }

  /** @param {String} value */
  static status(value) {
    return (
      this.statusMap[value.toUpperCase()] ?? 'info'
    )
  }

  /** @param {SubsReportItem} record */
  static toRow(record) {
    return this.keys.map(
      key => this.fields[key](record) ?? '-'
    )
  }

  /** @param {String} key @param {SubsReportItem} record Route target per field, built from raw record ids */
  static linkTo(key, record) {

    // Validate record
    if (!record) return null

    // Record props
    const { subscription, receipt, appointment } = record

    // Route target name and params
    let name = null
    let params = null

    switch (key) {

      // Package
      case labels.packageName:

        // Validate subscription id
        if (!subscription?.subscriptionId) break

        // Set route target
        name = 'SubscriberDetailPage'

        // Set route params
        params = {
          id: subscription.subscriptionId
        }
      break

      // Credit note
      case labels.creditNoteNo:

        // Validate credit note id
        if (!receipt?.creditNoteId) break

        // Set route target
        name = 'ReceiptDetailPage'

        // Set route params
        params = {
          id: receipt.creditNoteId
        }
      break

      // Receipt
      case labels.receiptNo:

        // Validate receipt id
        if (!receipt?.receiptId) break

        // Set route target
        name = 'ReceiptDetailPage'

        // Set route params
        params = {
          id: receipt.receiptId
        }
      break

      // Appointment
      case labels.performedAt:
      case labels.hasRemark:

        // Validate appointment id
        if (!appointment?.appointmentId) break

        // Set route target
        name = 'AppointmentDetailPage'

        // Set route params
        params = {
          id: appointment.appointmentId
        }
      break

      default:
        break
    }

    // Validate route target
    if (!(name && params)) return null

    return {
      name, params
    }
  }

  /** @param {String} key @param {SubsReportItem} record Image url per field, built from raw record */
  static imageTo(key, record) {

    // Validate record
    if (!record) return null

    // Record props
    const { receipt } = record

    switch (key) {

      // Bank transfer receipt image
      case labels.paymentChannel:
        return receipt?.imageTransfer || null

      default:
        return null
    }
  }
}
